import React, { useEffect, useMemo, useState } from "react";
import { DataGridPro, GridColDef, GridRowParams } from "@mui/x-data-grid-pro";

// 日本語ローカライズ
import { jaJPText } from "../stores/Mui_JapanesTxist";

// MUI
import {
  Alert,
  Autocomplete,
  Box,
  GlobalStyles,
  Button,
  Chip,
  Divider,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid";

// アイコン
import SearchIcon from "@mui/icons-material/Search";
import FilterAltOffRoundedIcon from "@mui/icons-material/FilterAltOffRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";

// ページコンポーネント
import ProsSalesDetails from "./Pros_management_popup";

// HTTP
import axios from "axios";

// Recoil（ログイン情報）
import { useRecoilValue } from "recoil";
import { LoginUserAtom } from "../stores/LoginUserAtom";

// 定数
import ConstList from "../utils/ConstList";

// テーマ
import { ThemeProvider } from "@mui/material/styles";
import theme from "../theme";

// =========================================
// 型定義
// =========================================

type StatusLabel = string;
type RecyclePriority = "高" | "中" | "低" | "対象外" | "未設定";

type Row = {
  id: number;
  company_id: number;
  sales_date: string;
  sales_status_id: number;
  status: StatusLabel;
  purchase_intention: string;
  company: string;
  company_name_missing: boolean;
  owner: string;
  next_action: string;
  proposal_details: string;

  // 画面には列として出さないが、行背景の判定用に保持する
  lost_reason_id: number | null;
  lost_reason_name: string;
  recycle_base_priority: RecyclePriority;
  is_lost: boolean;

  raw: any;
};

type Owner = {
  id: number;
  name: string;
};

const colors = {
  bg: "#2D2C3A",
  panel: "#1F2530",
  panel2: "#252B3A",
  panel3: "#30364A",
  header: "#1F1D30",
  border: "rgba(217, 217, 217, 0.12)",
  text: "#F5F7FA",
  muted: "#AAB4C3",
  accent: "#C5E763",
  accentSoft: "rgba(197, 231, 99, 0.12)",
  accentCell: "rgba(197, 231, 99, 0.16)",
  accentBorder: "rgba(197, 231, 99, 0.42)",
  danger: "#ff8579",
  dangerSoft: "rgba(255, 133, 121, 0.12)",
  dangerBorder: "rgba(255, 133, 121, 0.34)",
  lostRow: "rgba(255, 133, 121, 0.09)",
  lostRowHover: "rgba(255, 133, 121, 0.14)",
};

const normalizeStatus = (
  rawStatusId: any,
  rawStatusName: any
): StatusLabel | null => {
  const statusName = String(rawStatusName ?? "").trim();

  // master_sales_status.sales_status を最優先で表示する。
  // 固定ID変換だけにすると、マスタ側で名称を変更・追加した時に画面へ反映されない。
  if (statusName !== "") {
    return statusName;
  }

  const id = Number(rawStatusId ?? 0);

  if (id === 1) return "未着手";
  if (id === 2) return "応答なし";
  if (id === 3 || id === 4) return "進行中";
  if (id === 6) return "見送り";

  if (id > 0) return `ステータスID:${id}`;

  return null;
};

const normalizeText = (value: string | number | null | undefined) =>
  String(value ?? "").normalize("NFKC").toLowerCase();

const pickFirstText = (...values: any[]) => {
  for (const value of values) {
    const text = String(value ?? "").trim();
    if (text !== "") return text;
  }

  return "";
};

const formatDate = (value: string) => {
  if (!value) return "-";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");

  return `${yyyy}/${mm}/${dd}`;
};

const normalizeRecyclePriority = (
  rawPriority: any,
  lostReasonName: string
): RecyclePriority => {
  const priority = String(rawPriority ?? "").trim();

  if (
    priority === "高" ||
    priority === "中" ||
    priority === "低" ||
    priority === "対象外"
  ) {
    return priority;
  }

  const reason = normalizeText(lostReasonName);

  if (!reason) return "未設定";

  if (
    reason.includes("ニーズなし") ||
    reason.includes("ニーズ無") ||
    reason.includes("対象業種ではない") ||
    reason.includes("対象外")
  ) {
    return "対象外";
  }

  if (
    reason.includes("予算") ||
    reason.includes("タイミング") ||
    reason.includes("社内合意")
  ) {
    return "高";
  }

  if (
    reason.includes("競合") ||
    reason.includes("機能不足") ||
    reason.includes("要件不足")
  ) {
    return "中";
  }

  if (
    reason.includes("連絡不可") ||
    reason.includes("連絡が取れない") ||
    reason.includes("音信不通")
  ) {
    return "低";
  }

  return "未設定";
};

const isHighPurchaseIntention = (label: string) => {
  const text = normalizeText(label);

  return (
    text.includes("高") ||
    text.includes("強") ||
    text.includes("a") ||
    text.includes("あり") ||
    text.includes("有")
  );
};

const isIgnoredStatusFilter = (status: StatusLabel) => {
  const text = normalizeText(status);

  // このページの絞り込みでは未着手を候補に出さない。
  return text.includes("未着手") || text.includes("ステータスid:1");
};

// 失注になった行だけ色付けする
const getIsLost = ({
  rawSalesStatus,
  lostReasonName,
}: {
  rawSalesStatus: string;
  lostReasonName: string;
}) => {
  const rawStatusText = normalizeText(rawSalesStatus);
  const reasonText = normalizeText(lostReasonName);

  return (
    rawStatusText.includes("失注") ||
    rawStatusText.includes("見送り") ||
    rawStatusText.includes("停止") ||
    reasonText.includes("失注")
  );
};

const getStatusChipStyle = (status: StatusLabel, isLost: boolean) => {
  const text = normalizeText(status);

  if (isLost || text.includes("失注") || text.includes("見送り") || text.includes("停止")) {
    return {
      bg: colors.dangerSoft,
      fg: colors.danger,
      border: colors.dangerBorder,
    };
  }

  if (
    text.includes("商談") ||
    text.includes("進行") ||
    text.includes("提案") ||
    text.includes("見積")
  ) {
    return {
      bg: colors.accentSoft,
      fg: colors.accent,
      border: colors.accentBorder,
    };
  }

  return {
    bg: "rgba(255,255,255,0.055)",
    fg: colors.text,
    border: "rgba(255,255,255,0.12)",
  };
};

const FilterPanel = ({
  searchText,
  setSearchText,
  selectedStatuses,
  setSelectedStatuses,
  toggleStatus,
  statusOptions,
  statusCounts,
  ownerOptions,
  selectedOwners,
  setSelectedOwners,
  onClear,
  filteredCount,
  allCount,
}: {
  searchText: string;
  setSearchText: (value: string) => void;
  selectedStatuses: Set<StatusLabel>;
  setSelectedStatuses: React.Dispatch<React.SetStateAction<Set<StatusLabel>>>;
  toggleStatus: (status: StatusLabel) => void;
  statusOptions: StatusLabel[];
  statusCounts: Record<string, number>;
  ownerOptions: string[];
  selectedOwners: Set<string>;
  setSelectedOwners: React.Dispatch<React.SetStateAction<Set<string>>>;
  onClear: () => void;
  filteredCount: number;
  allCount: number;
}) => {
  const selectedOwnerValues = ownerOptions.filter((owner) =>
    selectedOwners.has(owner)
  );

  return (
    <Paper
      sx={{
        p: 2,
        borderRadius: 4,
        bgcolor: "rgba(31,37,48,0.96)",
        color: colors.text,
        border: `1px solid ${colors.border}`,
        boxShadow: "0 18px 44px rgba(0,0,0,0.24)",
        backdropFilter: "blur(10px)",
        height: { xs: "auto", md: "100%" },
        overflowY: { xs: "visible", md: "auto" },
      }}
    >
      <Stack spacing={2}>
        <Box>
          <Typography sx={{ fontSize: 15, fontWeight: 900 }}>
            絞り込み
          </Typography>
          <Typography sx={{ mt: 0.4, color: colors.muted, fontSize: 12 }}>
            表示件数：{filteredCount} / {allCount}
          </Typography>
        </Box>

        <TextField
          fullWidth
          label="企業名・担当・状況で検索"
          variant="outlined"
          size="small"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: colors.muted }} />
              </InputAdornment>
            ),
          }}
          sx={{
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "rgba(255,255,255,0.14)",
            },
            "& .MuiOutlinedInput-root": {
              borderRadius: "14px",
              backgroundColor: colors.panel2,
              color: colors.text,
              "&:hover": {
                backgroundColor: colors.panel3,
              },
              "&.Mui-focused": {
                backgroundColor: colors.panel3,
                boxShadow: "0 0 0 2px rgba(197,231,99,0.14)",
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: colors.accent,
              },
            },
            "& .MuiInputBase-input": {
              color: colors.text,
            },
            "& .MuiInputLabel-root": {
              color: colors.muted,
            },
            "& .MuiInputLabel-root.Mui-focused": {
              color: colors.accent,
            },
          }}
        />

        <Divider sx={{ borderColor: colors.border }} />

        <Box>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: 1 }}
          >
            <Typography sx={{ color: colors.muted, fontSize: 12, fontWeight: 800 }}>
              営業状況
            </Typography>

            <Chip
              size="small"
              label="すべて"
              clickable
              onClick={() => setSelectedStatuses(new Set<StatusLabel>())}
              sx={{
                height: 24,
                bgcolor: selectedStatuses.size === 0 ? colors.accentSoft : "rgba(255,255,255,0.04)",
                color: selectedStatuses.size === 0 ? colors.accent : colors.muted,
                border: `1px solid ${
                  selectedStatuses.size === 0 ? colors.accentBorder : "rgba(255,255,255,0.10)"
                }`,
                fontWeight: 800,
              }}
            />
          </Stack>

          {statusOptions.length === 0 ? (
            <Typography sx={{ color: colors.muted, fontSize: 12 }}>
              絞り込み対象の営業状況はありません。
            </Typography>
          ) : (
            <Stack direction="row" flexWrap="wrap" gap={0.8}>
              {statusOptions.map((status) => {
                const active = selectedStatuses.has(status);

                return (
                  <Chip
                    key={status}
                    label={`${status} ${statusCounts[status] ?? 0}`}
                    clickable
                    onClick={() => toggleStatus(status)}
                    sx={{
                      height: 28,
                      maxWidth: "100%",
                      bgcolor: active ? colors.accentSoft : "rgba(255,255,255,0.045)",
                      color: active ? colors.accent : colors.text,
                      border: `1px solid ${
                        active ? colors.accentBorder : "rgba(255,255,255,0.11)"
                      }`,
                      fontWeight: active ? 900 : 700,
                      "& .MuiChip-label": {
                        maxWidth: 150,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      },
                      "&:hover": {
                        bgcolor: colors.accentSoft,
                        color: colors.accent,
                      },
                    }}
                  />
                );
              })}
            </Stack>
          )}
        </Box>

        <Box>
          <Typography sx={{ mb: 1, color: colors.muted, fontSize: 12, fontWeight: 800 }}>
            案件担当者
          </Typography>

          <Autocomplete
            multiple
            disableCloseOnSelect
            size="small"
            options={ownerOptions}
            value={selectedOwnerValues}
            noOptionsText="担当者がありません"
            onChange={(_, value) => {
              setSelectedOwners(new Set(value));
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="担当者を選択"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "14px",
                    backgroundColor: colors.panel2,
                    color: colors.text,
                    "&:hover": {
                      backgroundColor: colors.panel3,
                    },
                    "&.Mui-focused": {
                      backgroundColor: colors.panel3,
                      boxShadow: "0 0 0 2px rgba(197,231,99,0.14)",
                    },
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "rgba(255,255,255,0.14)",
                  },
                  "& .MuiInputBase-input": {
                    color: colors.text,
                  },
                  "& .MuiChip-root": {
                    bgcolor: colors.accentSoft,
                    color: colors.accent,
                    border: `1px solid ${colors.accentBorder}`,
                    fontWeight: 700,
                  },
                  "& .MuiAutocomplete-popupIndicator, & .MuiAutocomplete-clearIndicator": {
                    color: colors.muted,
                  },
                }}
              />
            )}
          />
        </Box>

        <Box
          sx={{
            p: 1.35,
            borderRadius: 2.5,
            bgcolor: colors.dangerSoft,
            border: `1px solid ${colors.dangerBorder}`,
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <WarningAmberRoundedIcon sx={{ color: colors.danger, fontSize: 18 }} />
            <Box>
              <Typography sx={{ color: colors.text, fontSize: 12, fontWeight: 800 }}>
                赤系の行
              </Typography>
              <Typography sx={{ mt: 0.2, color: colors.muted, fontSize: 11.5, lineHeight: 1.6 }}>
                失注・見送り・停止扱いの案件です。
              </Typography>
            </Box>
          </Stack>
        </Box>

        <Button
          variant="outlined"
          startIcon={<FilterAltOffRoundedIcon />}
          onClick={onClear}
          sx={{
            borderColor: "rgba(255,255,255,0.18)",
            color: colors.text,
            borderRadius: 2.5,
            fontWeight: 800,
            "&:hover": {
              borderColor: colors.accent,
              backgroundColor: colors.accentSoft,
            },
          }}
        >
          条件をクリア
        </Button>
      </Stack>
    </Paper>
  );
};

export default function MasterProsManagement() {
  const loginUserData = useRecoilValue(LoginUserAtom);

  const [rows, setRows] = useState<Row[]>([]);
  const [ownersMaster, setOwnersMaster] = useState<Owner[]>([]);

  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // 詳細ポップアップ用
  const [popupOpen, setPopupOpen] = useState(false);
  const [selectedSalesRecordId, setSelectedSalesRecordId] = useState<number | null>(null);

  // フィルター
  const [searchText, setSearchText] = useState<string>("");
  const [selectedStatuses, setSelectedStatuses] = useState<Set<StatusLabel>>(
    new Set<StatusLabel>()
  );
  const [selectedOwners, setSelectedOwners] = useState<Set<string>>(new Set());

  const handleRowDoubleClick = (params: GridRowParams<Row>) => {
    setSelectedSalesRecordId(params.row.id);
    setPopupOpen(true);
  };

  const fetchData = async () => {
    setLoading(true);

    const postData = {
      flag: "Prospect_Activity_History",
      access_token: loginUserData?.accessToken,
      algorithm: loginUserData?.algorithm,
    };

    try {
      const response = await axios.post(
        ConstList.getApiUrl() + "ProspectiveManagement.php",
        postData,
        ConstList.AXIOS_CONFIG
      );

      if (response.data?.data === false) {
        setErrorMessage(response.data?.message || "取得に失敗しました。");

        if (response.data?.reDirect) {
          await ConstList.SLEEP(5000);
          window.location.href = response.data.reDirect;
        }

        return;
      }

      const rawList: any[] = response.data?.data?.mastersaleshistory ?? [];
      const ownerList: any[] = response.data?.data?.sales_person_list ?? [];

      const ownersMapped = ownerList
        .map((v: any, index: number) => ({
          id: Number(v?.sales_person_id ?? index + 1),
          name: String(v?.sales_person ?? "").trim(),
        }))
        .filter((owner) => owner.name !== "");

      setOwnersMaster(ownersMapped);

      const mapped = rawList
        .map((v: any): Row | null => {
          const rawSalesStatus = String(v?.sales_status ?? "");
          const status = normalizeStatus(v?.sales_status_id, rawSalesStatus);

          if (status === null) return null;

          const salesRecordId = Number(v?.id ?? v?.sales_record_id ?? 0);
          const companyId = Number(v?.company_id ?? v?.Company_id ?? 0);

          const companyName = pickFirstText(
            v?.company_name,
            v?.customer_name,
            v?.Company_name,
            v?.company
          );

          const lostReasonName = String(
            v?.lost_reason_name ??
              v?.lost_reason ??
              v?.Lost_reason_name ??
              ""
          );

          const recycleBasePriority = normalizeRecyclePriority(
            v?.recycle_base_priority ??
              v?.recycle_priority ??
              v?.Recycle_base_priority,
            lostReasonName
          );

          const lostReasonId =
            v?.lost_reason_id === null || v?.lost_reason_id === undefined
              ? null
              : Number(v?.lost_reason_id);

          return {
            id: salesRecordId,
            company_id: companyId,
            sales_date: String(v?.sales_date ?? ""),
            sales_status_id: Number(v?.sales_status_id ?? 0),
            status,
            purchase_intention: String(v?.purchase_intention ?? ""),
            company: companyName || "会社名未設定",
            company_name_missing: companyName === "",
            owner: String(v?.sales_person ?? ""),
            next_action: String(v?.next_action ?? ""),
            proposal_details: String(v?.proposed_service ?? ""),

            // 画面には列として出さない
            lost_reason_id: lostReasonId,
            lost_reason_name: lostReasonName,
            recycle_base_priority: recycleBasePriority,
            is_lost: getIsLost({
              rawSalesStatus,
              lostReasonName,
            }),

            raw: v,
          };
        })
        .filter((row): row is Row => row !== null);

      setRows(mapped);
      setErrorMessage(null);
    } catch (e: any) {
      console.error("データ取得エラー:", e);
      setErrorMessage(e?.message || "通信エラーが発生しました。");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const ownerOptions = useMemo(() => {
    const ownerNames = new Set<string>();

    ownersMaster.forEach((owner) => {
      if (owner.name) ownerNames.add(owner.name);
    });

    rows.forEach((row) => {
      if (row.owner) ownerNames.add(row.owner);
    });

    return Array.from(ownerNames).sort((a, b) => a.localeCompare(b, "ja"));
  }, [ownersMaster, rows]);

  const statusCounts = useMemo(() => {
    return rows.reduce<Record<string, number>>((acc, row) => {
      if (!row.status || isIgnoredStatusFilter(row.status)) return acc;

      acc[row.status] = (acc[row.status] ?? 0) + 1;

      return acc;
    }, {});
  }, [rows]);

  const statusOptions = useMemo(() => {
    return Object.keys(statusCounts).sort((a, b) => {
      const aLost = getIsLost({ rawSalesStatus: a, lostReasonName: "" });
      const bLost = getIsLost({ rawSalesStatus: b, lostReasonName: "" });

      if (aLost !== bLost) return aLost ? 1 : -1;

      return a.localeCompare(b, "ja");
    });
  }, [statusCounts]);

  const filteredRows = useMemo(() => {
    const q = normalizeText(searchText);

    return rows.filter((row) => {
      const hitSearch =
        !q ||
        normalizeText(row.company).includes(q) ||
        normalizeText(row.owner).includes(q) ||
        normalizeText(row.status).includes(q) ||
        normalizeText(row.purchase_intention).includes(q) ||
        normalizeText(row.next_action).includes(q) ||
        normalizeText(row.proposal_details).includes(q);

      const hitStatus = selectedStatuses.size === 0 || selectedStatuses.has(row.status);
      const hitOwner = selectedOwners.size === 0 || selectedOwners.has(row.owner);

      return hitSearch && hitStatus && hitOwner;
    });
  }, [rows, searchText, selectedStatuses, selectedOwners]);

  const toggleStatus = (status: StatusLabel) => {
    setSelectedStatuses((prev) => {
      const next = new Set(prev);

      if (next.has(status)) {
        next.delete(status);
      } else {
        next.add(status);
      }

      return next;
    });
  };

  const clearFilters = () => {
    setSearchText("");
    setSelectedStatuses(new Set<StatusLabel>());
    setSelectedOwners(new Set());
  };

  const highPurchaseCount = useMemo(
    () => filteredRows.filter((row) => isHighPurchaseIntention(row.purchase_intention)).length,
    [filteredRows]
  );

  const lostCount = useMemo(
    () => filteredRows.filter((row) => row.is_lost).length,
    [filteredRows]
  );

  // =========================================
  // カラム
  // 再アプローチ列・失注停止理由列は持たせない
  // 失注行だけ背景色を変更
  // 購入意思が高いセルのみ背景色を変更
  // =========================================

  const columns = useMemo<GridColDef<Row>[]>(() => {
    return [
      {
        field: "sales_date",
        headerName: "コンタクト日",
        flex: 0.75,
        minWidth: 120,
        renderCell: (params) => (
          <Typography sx={{ color: colors.text, fontSize: 13, fontVariantNumeric: "tabular-nums" }}>
            {formatDate(params.row.sales_date)}
          </Typography>
        ),
      },
      {
        field: "company",
        headerName: "企業名",
        flex: 1.35,
        minWidth: 190,
        renderCell: (params) => (
          <Tooltip
            title={
              params.row.company_name_missing
                ? "sales_history.company_id と master_customer.company_id の紐づきが確認できません"
                : params.row.company
            }
          >
            <Typography
              sx={{
                color: params.row.company_name_missing ? colors.danger : colors.text,
                fontWeight: 900,
                fontSize: 13.5,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {params.row.company}
            </Typography>
          </Tooltip>
        ),
      },
      {
        field: "status",
        headerName: "状況",
        flex: 0.85,
        minWidth: 140,
        renderCell: (params) => {
          const style = getStatusChipStyle(params.row.status, params.row.is_lost);

          return (
            <Tooltip title={params.row.status}>
              <Chip
                label={params.row.status}
                size="small"
                sx={{
                  width: 108,
                  maxWidth: 108,
                  height: 28,
                  borderRadius: "9999px",
                  bgcolor: style.bg,
                  color: style.fg,
                  border: `1px solid ${style.border}`,
                  fontWeight: 900,
                  "& .MuiChip-label": {
                    width: "100%",
                    textAlign: "center",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  },
                }}
              />
            </Tooltip>
          );
        },
      },
      {
        field: "purchase_intention",
        headerName: "購入意思",
        flex: 0.85,
        minWidth: 120,
        cellClassName: (params) =>
          isHighPurchaseIntention(String(params.value ?? "")) ? "cell-purchase-high" : "",
        renderCell: (params) => {
          const label = params.row.purchase_intention || "未設定";
          const isHigh = isHighPurchaseIntention(label);

          return (
            <Typography
              sx={{
                color: isHigh ? colors.accent : colors.text,
                fontWeight: isHigh ? 900 : 600,
                fontSize: 13,
              }}
            >
              {label}
            </Typography>
          );
        },
      },
      {
        field: "owner",
        headerName: "案件担当者",
        flex: 0.9,
        minWidth: 130,
        renderCell: (params) => (
          <Typography
            sx={{
              color: params.row.owner ? colors.text : colors.muted,
              fontSize: 13,
              fontWeight: 700,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            title={params.row.owner}
          >
            {params.row.owner || "-"}
          </Typography>
        ),
      },
      {
        field: "next_action",
        headerName: "次回アクション",
        flex: 1,
        minWidth: 150,
        renderCell: (params) => (
          <Typography
            sx={{
              color: params.row.next_action ? colors.text : colors.muted,
              fontSize: 13,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            title={params.row.next_action}
          >
            {params.row.next_action || "-"}
          </Typography>
        ),
      },
      {
        field: "proposal_details",
        headerName: "提案サービス",
        flex: 1,
        minWidth: 140,
        renderCell: (params) => (
          <Typography
            sx={{
              color: params.row.proposal_details ? colors.text : colors.muted,
              fontSize: 13,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            title={params.row.proposal_details}
          >
            {params.row.proposal_details || "-"}
          </Typography>
        ),
      },
    ];
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <GlobalStyles
        styles={{
          html: {
            overflowY: "hidden",
          },
          body: {
            overflowY: "hidden",
          },
          "#root": {
            overflowY: "hidden",
          },
        }}
      />
      <Box
        sx={{
          // このページ自身はスクロールさせず、DataGrid の中だけをスクロールさせる。
          // 右端に出るブラウザ側のスクロールバーは body/html 側の overflow が原因なので、
          // 上の GlobalStyles とセットで止める。
          height: {
            xs: "auto",
            md: "calc(100dvh - 150px)",
          },
          maxHeight: {
            xs: "none",
            md: "calc(100dvh - 150px)",
          },
          minHeight: 0,
          overflow: {
            xs: "visible",
            md: "hidden",
          },
          overscrollBehavior: "none",
          bgcolor: colors.bg,
          color: colors.text,
          p: { xs: 1.5, md: 2 },
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {errorMessage && (
          <Alert
            severity="error"
            sx={{
              mb: 2,
              bgcolor: colors.dangerSoft,
              color: colors.text,
              border: `1px solid ${colors.dangerBorder}`,
              "& .MuiAlert-icon": {
                color: colors.danger,
              },
            }}
          >
            {errorMessage}
          </Alert>
        )}

        <Grid
          container
          spacing={2.2}
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: "hidden",
          }}
        >
          {/* 左カラム：フィルター */}
          <Grid
            size={{ xs: 12, md: 3 }}
            sx={{
              height: { xs: "auto", md: "100%" },
              minHeight: 0,
              overflow: { xs: "visible", md: "hidden" },
            }}
          >
            <FilterPanel
              searchText={searchText}
              setSearchText={setSearchText}
              selectedStatuses={selectedStatuses}
              setSelectedStatuses={setSelectedStatuses}
              toggleStatus={toggleStatus}
              statusOptions={statusOptions}
              statusCounts={statusCounts}
              ownerOptions={ownerOptions}
              selectedOwners={selectedOwners}
              setSelectedOwners={setSelectedOwners}
              onClear={clearFilters}
              filteredCount={filteredRows.length}
              allCount={rows.length}
            />
          </Grid>

          {/* 右カラム：一覧 */}
          <Grid
            size={{ xs: 12, md: 9 }}
            sx={{
              height: { xs: "68vh", md: "100%" },
              minHeight: 0,
              overflow: "hidden",
            }}
          >
            <Paper
              sx={{
                height: "100%",
                overflow: "hidden",
                borderRadius: 4,
                bgcolor: colors.panel,
                border: `1px solid ${colors.border}`,
                boxShadow: "0 18px 48px rgba(0,0,0,0.24)",
              }}
            >
              <DataGridPro<Row>
                rows={filteredRows}
                columns={columns}
                density="standard"
                disableRowSelectionOnClick
                pageSizeOptions={[10, 20, 50]}
                loading={loading}
                initialState={{
                  pagination: { paginationModel: { page: 0, pageSize: 10 } },
                }}
                localeText={{
                  ...jaJPText,
                  footerTotalRows: "総件数：",
                }}
                getRowClassName={(params) =>
                  params.row.is_lost ? "row-lost" : ""
                }
                sx={{
                  height: "100%",
                  border: "none",
                  color: colors.text,
                  bgcolor: colors.panel,
                  "& .MuiDataGrid-columnHeaders": {
                    bgcolor: colors.header,
                    color: colors.text,
                    borderBottom: `1px solid ${colors.border}`,
                  },
                  "& .MuiDataGrid-columnHeaderTitle": {
                    fontWeight: 900,
                    fontSize: 13,
                  },
                  "& .MuiDataGrid-cell": {
                    borderBottom: `1px solid ${colors.border}`,
                    outline: "none !important",
                  },
                  "& .MuiDataGrid-row": {
                    bgcolor: colors.panel,
                    transition: "background-color .15s ease",
                  },
                  "& .MuiDataGrid-row:hover": {
                    bgcolor: "rgba(255,255,255,0.045)",
                  },
                  "& .row-lost": {
                    bgcolor: `${colors.lostRow} !important`,
                    boxShadow: `inset 4px 0 0 ${colors.danger}`,
                  },
                  "& .row-lost:hover": {
                    bgcolor: `${colors.lostRowHover} !important`,
                  },
                  "& .cell-purchase-high": {
                    bgcolor: `${colors.accentCell} !important`,
                    boxShadow: `inset 3px 0 0 ${colors.accent}`,
                  },
                  "& .MuiDataGrid-footerContainer": {
                    color: colors.muted,
                    borderTop: `1px solid ${colors.border}`,
                    bgcolor: colors.header,
                  },
                  "& .MuiTablePagination-root": {
                    color: colors.muted,
                  },
                  "& .MuiDataGrid-overlay": {
                    bgcolor: colors.panel,
                    color: colors.muted,
                  },
                }}
                onRowDoubleClick={handleRowDoubleClick}
              />
            </Paper>
          </Grid>
        </Grid>

        <ProsSalesDetails
          EditOpen={popupOpen}
          SetEditOpen={setPopupOpen}
          SalesRecordId={selectedSalesRecordId}
          onChanged={fetchData}
        />
      </Box>
    </ThemeProvider>
  );
}
